import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../apiClient';

const CreateBulkRefund = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        invoiceDetail: '',
        outageStartAt: '',
        outageEndAt: '',
        compensationDays: 0,
        targetPlans: ['lite', 'standard', 'business'],
        calculationType: 'auto',
        manualAmountEx: 0,
        reason: 'システム障害',
        memo: ''
    });

    useEffect(() => {
        if (formData.outageStartAt && formData.outageEndAt) {
            const start = new Date(formData.outageStartAt);
            const end = new Date(formData.outageEndAt);
            const diffMs = end - start;
            if (diffMs > 0) {
                const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                setFormData(prev => ({ ...prev, compensationDays: days }));
            }
        }
    }, [formData.outageStartAt, formData.outageEndAt]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.compensationDays < 1 && formData.calculationType === 'auto') {
            alert('補償日数は1日以上必要です。');
            return;
        }

        setLoading(true);
        try {
            const response = await apiClient.post('/v1/admin/bulk-refunds', formData);
            if (response.ok) {
                const { outageId } = await response.json();
                navigate(`/admin/bulk-refunds/${outageId}`);
            } else {
                alert('作成に失敗しました。');
            }
        } catch (error) {
            console.error(error);
            alert('エラーが発生しました。');
        } finally {
            setLoading(false);
        }
    };

    const togglePlan = (plan) => {
        setFormData(prev => ({
            ...prev,
            targetPlans: prev.targetPlans.includes(plan)
                ? prev.targetPlans.filter(p => p !== plan)
                : [...prev.targetPlans, plan]
        }));
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-3xl">
            <h2 className="text-2xl font-bold mb-6">一括返金の新規作成</h2>
            
            <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">障害名（管理用）</label>
                    <input 
                        type="text" 
                        required
                        className="mt-1 block w-full border rounded-md shadow-sm p-2"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="例: DB障害によるAPI停止"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">返金明細（請求書に表示）</label>
                    <input 
                        type="text" 
                        required
                        className="mt-1 block w-full border rounded-md shadow-sm p-2"
                        value={formData.invoiceDetail}
                        onChange={e => setFormData({ ...formData, invoiceDetail: e.target.value })}
                        placeholder="例: 障害発生による補償 (2026/07/01)"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">障害開始日時</label>
                        <input 
                            type="datetime-local" 
                            required
                            className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            value={formData.outageStartAt}
                            onChange={e => setFormData({ ...formData, outageStartAt: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">障害終了日時</label>
                        <input 
                            type="datetime-local" 
                            required
                            className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            value={formData.outageEndAt}
                            onChange={e => setFormData({ ...formData, outageEndAt: e.target.value })}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">補償日数（自動計算後も編集可）</label>
                        <input 
                            type="number" 
                            className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            value={formData.compensationDays}
                            onChange={e => setFormData({ ...formData, compensationDays: parseInt(e.target.value) || 0 })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">返金方法</label>
                        <select 
                            className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            value={formData.calculationType}
                            onChange={e => setFormData({ ...formData, calculationType: e.target.value })}
                        >
                            <option value="auto">自動計算 (月額 / 30 × 日数)</option>
                            <option value="manual">全社一律の手動入力</option>
                        </select>
                    </div>
                </div>

                {formData.calculationType === 'manual' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">一律返金額（税別）</label>
                        <input 
                            type="number" 
                            className="mt-1 block w-full border rounded-md shadow-sm p-2"
                            value={formData.manualAmountEx}
                            onChange={e => setFormData({ ...formData, manualAmountEx: parseInt(e.target.value) || 0 })}
                        />
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">対象プラン</label>
                    <div className="flex flex-wrap gap-4">
                        {['free', 'lite', 'standard', 'business'].map(plan => (
                            <label key={plan} className="flex items-center space-x-2">
                                <input 
                                    type="checkbox" 
                                    checked={formData.targetPlans.includes(plan)}
                                    onChange={() => togglePlan(plan)}
                                    className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                />
                                <span className="text-sm text-gray-700 uppercase">{plan}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">管理メモ</label>
                    <textarea 
                        className="mt-1 block w-full border rounded-md shadow-sm p-2"
                        rows="3"
                        value={formData.memo}
                        onChange={e => setFormData({ ...formData, memo: e.target.value })}
                    ></textarea>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button 
                        type="button" 
                        onClick={() => navigate('/admin/bulk-refunds')}
                        className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                    >
                        キャンセル
                    </button>
                    <button 
                        type="submit" 
                        disabled={loading}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-400 font-bold"
                    >
                        {loading ? '作成中...' : '対象企業を抽出して次へ'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateBulkRefund;
